import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col, Input } from "reactstrap";
import "@assets/scss/custom/components/tables.scss";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import Spinners from "./Spinner";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { EPISODE_LIST } from "../../constants/constants";
// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input
          {...props}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Col>
    </React.Fragment>
  );
};

export const CustomTable = ({
  loading,
  columns,
  data,
  tableClass,
  theadClass,
  mediaId,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationLimit,
  SearchPlaceholder,
  handleList,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  pageId,
  mediaCurrentPage,
  isEpisode,
  isAddPermission
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel } = table;
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(10);
  const [contentPageSize, setContentpageSize] = useState(10);
  const pageQuery = parseInt(searchParams.get("current_page")) || 1;
  const contentPage = parseInt(searchParams.get("content_page"));
  const [pageIndex, setPageIndex] = useState(pageQuery - 1);
  const [contentPageIndex, setContentPageIndex] = useState(contentPage - 1);
  const responseMessage = useSelector((state) => state.delete.responseMessage);
  const publishMessage = useSelector((state) => state.publish.responseMessage);
  useEffect(() => {
    if (responseMessage !== null || publishMessage !== null) {
      FetchData(
        isAddButton ? pageIndex + 1 : contentPageIndex + 1,
        pageSize,
        globalFilter,
        pageId
      );
    }
  }, [responseMessage, publishMessage]);

  useEffect(() => {
    if (!pageId) {
      FetchData(pageQuery ? pageQuery : 1, pageSize, globalFilter, pageId);
    } else {
      FetchData(
        isAddButton
          ? pageQuery
            ? pageQuery
            : 1
          : contentPage
            ? contentPage
            : 1,
        pageSize,
        globalFilter,
        pageId
      );
    }
  }, [pageQuery, pageSize, globalFilter, pageId]);

  const FetchData = (
    page = 1,
    limit = pageSize,
    filter = globalFilter,
    id = pageId,
    updatePageQuery = true
  ) => {
    dispatch(
      handleList({ pageCount: page, limit: limit, search: filter, id: id })
    );
    if (!isAddButton) {
      const params = new URLSearchParams({});
      params.set("current_page", mediaCurrentPage);
      params.set("content_page", page);
      navigate({ search: params.toString() });
    }

    if (!pageId) {
      if (updatePageQuery && !filter && page > 1) {
        const params = new URLSearchParams({ current_page: page });

        if (id) {
          params.set("id", id);
        }

        navigate({ search: params.toString() });
      } else {
        const params = new URLSearchParams();
        if (id) {
          params.set("id", id);
        }
        navigate({ search: params.toString() });
        setPageIndex(0);
        setContentPageIndex(0);
      }
    }
  };

  const startRow = isAddButton
    ? pageIndex * pageSize + 1
    : contentPageIndex * contentPageSize + 1;

  const endRow = isAddButton
    ? Math.min(pageIndex + 1 * pageSize, paginationLimit?.total_count || 0)
    : Math.min(
      contentPageIndex + 1 * contentPageSize,
      paginationLimit?.total_count || 0
    );
  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1;

    if (isAddButton) {
      setPageIndex(data.selected);
    } else {
      setContentPageIndex(data.selected);
    }

    FetchData(selectedPage, pageSize, globalFilter, pageId, false);
  };
  return (
    <Fragment>
      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);

                setContentpageSize(newSize);
                // table.setContentpageSize(newSize);
                table.setPageSize(newSize);
                setContentPageIndex(0);
                setPageIndex(0);

                FetchData(1, newSize, globalFilter, pageId);
              }}
            >
              {isEpisode === true ? [10, 100, 200, 300, 400, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>

              )) : [10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}

            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => {
              setGlobalFilter(String(value));
            }}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
          />
        )}

        {isAddButton && isAddPermission &&
        (
        <Col sm={6}>
          <div className="text-sm-end">
            <Button
              type="button"
              className={buttonClass}
              onClick={handleUserClick}
            >
              <i className="mdi mdi-plus me-1"></i> {buttonName}
            </Button>
          </div>
        </Col>
        )}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table
          hover
          className={`${tableClass} table-bordered`}
          bordered={isBordered}
        >
          <thead className="table-info">
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <th>S.NO</th>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${header.column.columnDef.enableSorting
                        ? "sorting sorting_desc"
                        : ""
                        }`}
                    >
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
                {/* <th>Action</th> */}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length}>
                  <Spinners />
                </td>
              </tr>
            ) : data.length ? (
              getRowModel().rows.map((row, index) => {
                const displayedIndex = startRow + index;
                return (
                  <tr key={row.id}>
                    <td>{displayedIndex}</td>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                    {/* <td>{renderRowActions(row)}</td> */}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1}>Data not found</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {data?.length ? (
        <>
          {isPagination && (
            <Row>
              <Col sm={12} md={5}>
                <div className="dataTables_info">
                  Showing {startRow} to {endRow} of{" "}
                  {paginationLimit?.total_count}
                  {""} Entries
                </div>
              </Col>
              <Col sm={12} md={7}>
                {paginationLimit?.total_count && (
                  <ReactPaginate
                    pageCount={Math.ceil(
                      paginationLimit?.total_count / pageSize
                    )}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={3}
                    // onPageChange={handlePageChange}
                    onPageChange={(data) => {
                      if (isAddButton) {
                        setPageIndex(data.selected);
                      } else {
                        setContentPageIndex(data.selected);
                      }

                      FetchData(data.selected + 1, pageSize, globalFilter);
                    }}
                    forcePage={!isAddButton ? contentPageIndex : pageIndex}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                    previousLabel={"Prev"}
                    nextLabel={"Next"}
                  />
                )}
              </Col>
            </Row>
          )}
        </>
      ) : null}
    </Fragment>
  );
};
